import {
  MessageEntity,
  MessageOptions,
  MessageOptionType,
} from 'src/store/chatApi';
import { useState } from 'react';
import Autocomplete from 'react-google-autocomplete';
import SuggestionCard, {
  SuggestionCardData,
} from '../matchmaker-chat/suggestionCard';
import { ChatBubble } from 'src/ui/ChatBubble';
import { Button } from 'src/ui/Button';
import { Input } from 'src/ui/Input';
import { Select } from 'src/ui/Select';
import { Icon } from 'src/ui/Icon';

export interface MessageRowProps {
  message: MessageEntity;
  isCurrentUser: boolean;
  onSendWithOption: (message: Partial<MessageEntity>) => void;
}

const extractLocationDetails = (place: google.maps.places.PlaceResult) => {
  const addressComponents = place.address_components;
  const location = place.geometry?.location;
  const details = {
    city: '',
    state: '',
    country: '',
    latitude: location ? location.lat() : 0,
    longitude: location ? location.lng() : 0,
    formattedAddress: place.formatted_address,
    postalCode: '',
  };

  addressComponents?.forEach((component) => {
    const types = component.types;
    if (types.includes('locality')) {
      details.city = component.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      details.state = component.short_name;
    }
    if (types.includes('country')) {
      details.country = component.short_name;
    }
    if (types.includes('postal_code')) {
      details.postalCode = component.long_name;
    }
  });

  return details;
};

const MessageRow = ({
  message,
  isCurrentUser,
  onSendWithOption,
}: MessageRowProps) => {
  const { options } = message;
  const { labels, default1, optionType, default2, disableOther, placeholder } =
    (options || {}) as MessageOptions;
  const [otherText, setOtherText] = useState<string>('');
  const [place, setPlace] = useState<google.maps.places.PlaceResult | null>(
    null
  );

  const [selected, setSelected] = useState<Set<number>>(
    typeof default1 === 'number' ? new Set([default1]) : new Set()
  );
  const [selected2, setSelected2] = useState<number | undefined>(default2);

  const hasOther = selected?.has(0);

  const onSend = () => {
    const newMessage: Partial<MessageEntity> = {
      selectedOptions: Array.from(selected),
    };
    switch (optionType) {
      case MessageOptionType.Dropdown:
      case MessageOptionType.Single:
      case MessageOptionType.Multiple: {
        newMessage.text = labels
          .filter((item) => selected.has(item[1]))
          .map((item) => item[0])
          .join(', ');
        break;
      }
      case MessageOptionType.Range: {
        if (!selected2) return;
        newMessage.text = labels
          .filter((item) => selected.has(item[1]))
          .map((item) => item[0])
          .join(', ');
        newMessage.text += ` - ${
          labels.find((item) => item[1] === selected2)?.[0]
        }`;
        newMessage.selectedOptions?.push(selected2);
        break;
      }
      case MessageOptionType.Location: {
        if (!place) return;
        const {
          city,
          state,
          country,
          latitude,
          longitude,
          formattedAddress,
          postalCode,
        } = extractLocationDetails(place);
        newMessage.text = formattedAddress;
        newMessage.selectedOptions = [
          latitude,
          longitude,
          city,
          state,
          country,
          postalCode,
        ];
        break;
      }
    }
    if (hasOther) {
      newMessage.otherString = otherText;
      newMessage.text += `\n${otherText}`;
    }
    onSendWithOption(newMessage);
  };

  const onClick = (selection: number) => {
    let newSelected: Set<number>;
    switch (optionType) {
      case MessageOptionType.Range:
      case MessageOptionType.Dropdown:
      case MessageOptionType.Single: {
        newSelected = new Set([selection]);
        break;
      }
      case MessageOptionType.Multiple: {
        newSelected = new Set(selected);
        if (newSelected.has(selection)) {
          newSelected.delete(selection);
        } else {
          newSelected.add(selection);
        }
        break;
      }
      default: {
        newSelected = new Set([selection]);
        break;
      }
    }
    setSelected(newSelected);
  };

  const renderSelection = () => (
    <div className="col gap-2">
      {labels?.map((label) => (
        <div
          key={label[1]}
          className="row gap-2"
          style={{ cursor: 'pointer', alignItems: 'center' }}
          onClick={() => onClick(label[1])}
        >
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: optionType === MessageOptionType.Single ? '50%' : 3,
              border: `2px solid ${
                selected.has(label[1])
                  ? 'var(--accent)'
                  : 'var(--border-strong)'
              }`,
              background: selected.has(label[1])
                ? 'var(--accent)'
                : 'transparent',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {selected.has(label[1]) && (
              <Icon name="check" size={10} color="white" strokeWidth={3} />
            )}
          </span>
          <span style={{ fontSize: 'var(--fs-sm)' }}>{label[0]}</span>
        </div>
      ))}
      {hasOther && !disableOther && (
        <Input
          size="sm"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder={placeholder || 'Other'}
        />
      )}
    </div>
  );

  const renderDropdown = () => {
    const selectedLabel = labels.find(
      (label) => label[1] === Array.from(selected)[0]
    );
    return (
      <Select
        size="sm"
        value={selectedLabel?.[1]}
        onChange={(e) => onClick(Number(e.target.value))}
      >
        {labels?.map((label) => (
          <option value={label[1]} key={label[1]}>
            {label[0]}
          </option>
        ))}
      </Select>
    );
  };

  const renderRange = () => {
    const selectedLabel = labels.find(
      (label) => label[1] === Array.from(selected)[0]
    );
    const selectedLabel2 = labels.find((label) => label[1] === selected2);
    return (
      <div className="row gap-2" style={{ alignItems: 'center' }}>
        <Select
          size="sm"
          className="grow"
          value={selectedLabel?.[1]}
          onChange={(e) => onClick(Number(e.target.value))}
        >
          {labels?.map((label) => (
            <option value={label[1]} key={label[1]}>
              {label[0]}
            </option>
          ))}
        </Select>
        <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
          -
        </span>
        <Select
          size="sm"
          className="grow"
          value={selectedLabel2?.[1]}
          onChange={(e) => setSelected2(Number(e.target.value))}
        >
          {labels?.map((label) => (
            <option value={label[1]} key={label[1]}>
              {label[0]}
            </option>
          ))}
        </Select>
      </div>
    );
  };

  const renderLocation = () => (
    <Autocomplete
      apiKey="AIzaSyBOv3-NC2CUYuTHzog4qX5H79RnEgrx7L4"
      onPlaceSelected={(place) => setPlace(place)}
    />
  );

  const renderOptions = () => {
    let rendering: React.ReactNode = null;
    switch (optionType) {
      case MessageOptionType.Single:
      case MessageOptionType.Multiple: {
        rendering = renderSelection();
        break;
      }
      case MessageOptionType.Dropdown: {
        rendering = renderDropdown();
        break;
      }
      case MessageOptionType.Range: {
        rendering = renderRange();
        break;
      }
      case MessageOptionType.Location: {
        rendering = renderLocation();
        break;
      }
      case MessageOptionType.Confirm: {
        if (default1 === -1) {
          return (
            <div
              style={{
                fontSize: 'var(--fs-sm)',
                color: 'var(--green-text)',
                padding: '6px 0',
              }}
            >
              All Interview Completed
            </div>
          );
        }
        return (
          <div className="row gap-2" style={{ marginTop: 8, flexWrap: 'wrap' }}>
            {labels?.map((label) => (
              <Button
                key={label[1]}
                variant="secondary"
                size="sm"
                onClick={() =>
                  onSendWithOption({
                    text: label[0],
                    selectedOptions: [label[1]],
                  })
                }
              >
                {label[0]}
              </Button>
            ))}
          </div>
        );
      }
    }
    if (!rendering) return null;
    return (
      <div className="col gap-2" style={{ marginTop: 8 }}>
        {rendering}
        <Button variant="primary" size="sm" onClick={onSend}>
          Send
        </Button>
      </div>
    );
  };

  return (
    <ChatBubble dir={isCurrentUser ? 'out' : 'in'}>
      <span>{message.text}</span>
      {options?.suggestion && (
        <SuggestionCard data={options.suggestion as SuggestionCardData} />
      )}
      {renderOptions()}
    </ChatBubble>
  );
};

export default MessageRow;
